<template>
  <div class="w-full">
    <div v-if="!reportModalOpen" class="w-full flex flex-wrap lg:flex-no-wrap">
      <div class="w-full lg:w-1/3 lg:pr-8 lg:border-r lg:border-truegray-300">
        <!-- Profile ProfileImage/Police Check -->
        <div class="flex justify-center lg:justify-start">
          <div class="w-10/12 relative mb-12" :style="{ maxWidth: '155px' }">
            <ProfileImage :connection="connection" />
          </div>
        </div>
        <div v-if="connection.isExampleModal" class="">
          <h3 class="text-sm text-cw-alt mb-1">EXAMPLE PROFILE</h3>
        </div>
        <div class="flex flex-col space-y-6">
          <div>
            <h3 class="text-xl mb-2">{{ connection.name }}</h3>
            <p class="text-xs text-truegray-500">
              {{ connection.company }} - {{ connection.job_title }}
            </p>
          </div>

          <div>
            <div v-if="connection.region" class="flex space-x-2">
              <img src="/img/connections/icons/region.svg" alt="Region" />
              <p class="text-truegray-700">{{ connection.region?.title }}</p>
            </div>
            <div v-else-if="!!connection.regions?.length">
              <MultipleTags
                :is-regions="true"
                :tags="connection.regions.map((i) => i.title)"
                :colour="'text-truegray-700'"
                :titles="true"
                :flex="false"
                :font-heading="false"
                :truncate="true"
              />
            </div>
            <div v-if="connection.website" class="flex space-x-2">
              <img src="/img/connections/icons/website.svg" alt="Website" />
              <p class="text-truegray-700">{{ connection.website }}</p>
            </div>
          </div>

          <div
            v-if="hasAdditionalContactInfo()"
            class="flex space-x-4"
            :class="isExampleModal ? 'cursor-not-allowed' : ''"
          >
            <div
              v-if="hasAdditionalContactInfo(CONTACT_KEYS.phone)"
              title="Phone number"
              class="w-10 h-10 rounded-full bg-white border border-truegray-700 transition-all flex items-center justify-center"
              :class="
                isExampleModal
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer hover:border-primary hover:border-2'
              "
              @click="isExampleModal ? null : viewContactInfo('phone')"
            >
              <img
                src="/img/connections/icons/phone.png"
                alt="Phone Number Logo"
              />
            </div>
            <div
              v-if="hasAdditionalContactInfo(CONTACT_KEYS.linkedIn)"
              title="LinkedIn"
              class="w-10 h-10 rounded-full cursor-pointer bg-white border border-truegray-700 hover:border-primary hover:border-2 transition-all flex items-center justify-center"
              @click="viewContactInfo(CONTACT_KEYS.linkedIn)"
            >
              <img
                src="/img/connections/icons/linkedin.png"
                alt="LinkedIn Logo"
              />
            </div>
          </div>

          <ul>
            <li
              v-for="(item, i) in menuItems"
              :key="item.label"
              class="relative"
            >
              <div
                class="flex space-x-2 transition-all p-2 relative cursor-pointer right-2 hover:bg-truegray-100 rounded"
                :class="[
                  isActiveMenuItem(item) ? 'bg-truegray-100 font-bold' : '',
                  !isMessageTabOpen && item.isMessageTab ? 'hidden' : '',
                ]"
                @click="setActiveMenuItem(i)"
              >
                <img :src="item.icon" :alt="item.label + ' icon'" />
                <p>{{ item.label }}</p>
              </div>
            </li>
          </ul>

          <button
            class="btn btn-trans-black lg:mr-6 transition-all"
            :class="isMessageTabOpen ? 'hidden' : 'visible'"
            @click="setActiveMenuItem(2)"
          >
            Message
          </button>
        </div>
      </div>
      <div
        class="w-full lg:w-2/3 lg:pl-8 lg:pr-6 pt-6 mt-6 lg:mt-0 lg:pt-0 lg:border-none border-t border-truegray-300"
      >
        <Bio v-if="activeMenuItem === 0" :connection="connection" />
        <History v-if="activeMenuItem === 1" :connection="connection" />
        <Message
          v-if="activeMenuItem === 2"
          :connection="connection"
          :example="isExampleModal"
        />
      </div>
    </div>
    <div v-else class="">
      <ModalReport :config="reportModalData" />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router/composables'
import { useStore } from '../../../../../js/store/index.js'
import Bio from './ModalProfileBio.vue'
import History from './ModalProfileHistory.vue'
import Message from './ModalProfileMessage.vue'
import ProfileImage from '../ImageConnection.vue'
import ModalReport from './ModalReport.vue'
import MultipleTags from '../../../../../shared/components/MultipleTags.vue'
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import {
  URL_PROTOCOLS_HTTP,
  validateURLProtocol,
  validateURL,
  updateURLWithProtocol,
} from '../../../../../js/util/helper.js'

export default defineComponent({
  name: 'ModalProfile',

  components: {
    Bio,
    History,
    Message,
    ProfileImage,
    ModalReport,
    MultipleTags,
  },

  props: {
    config: { type: Object, default: null },
  },

  setup(props) {
    const route = useRoute()
    const store = useStore()

    const userAccess = computed(() => store.getters['shared/getUserAccess'])
    const isContributor = computed(() => store.getters['shared/isContributor'])
    const isManagerContext = computed(() =>
      store.getters['connections/isManagerContext'](route.fullPath),
    )

    const connection = ref(props.config?.connection || null)
    const isExampleModal = ref(props.config?.connection?.isExampleModal || null)

    const CONTACT_KEYS = {
      linkedIn: 'social_linkedIn',
      phone: 'phone',
    }

    const hasAdditionalContactInfo = (type = null) => {
      if (type) {
        return connection.value[type]
      }

      return Object.values(CONTACT_KEYS).some(
        (value) => !!connection.value[value],
      )
    }

    const viewContactInfo = (type) => {
      let value = connection.value[type]
      if (type === CONTACT_KEYS.linkedIn) {
        if (!validateURLProtocol(value, URL_PROTOCOLS_HTTP)) {
          value = updateURLWithProtocol(value)
        }

        if (validateURL(value)) {
          window.open(value, '_blank')
        }
        if (!isContributor.value) {
          store.dispatch('connections/dispatchLogContactLinkedIn', {
            connectionId: connection.value.id,
          })
        }
      } else {
        // Putting it before clipboard due to http behaviour in local environments
        if (!isContributor.value) {
          store.dispatch('connections/dispatchLogContactPhoned', {
            connectionId: connection.value.id,
          })
        }
        navigator.clipboard.writeText(value)
        EventBus.$emit('triggerAlertResponse', {
          status: 'success',
          message: 'Phone number copied to clipboard',
        })
      }
    }

    const menuItems = computed(() => {
      const items = [
        {
          label: 'Bio',
          icon: '/img/connections/icons/user.svg',
        },
        {
          label: 'Send Message',
          icon: '/img/connections/icons/history.svg',
          isMessageTab: true,
        },
      ]

      const reportItem = {
        label: 'Report History',
        icon: '/img/connections/icons/history.svg',
      }

      if (userAccess.value && !isContributor.value && isManagerContext.value) {
        items.splice(1, 0, reportItem)
      }

      return items
    })

    const activeMenuItem = ref(0)
    const isActiveMenuItem = (item) => {
      return item === menuItems.value[activeMenuItem.value]
    }
    const setActiveMenuItem = (i) => {
      activeMenuItem.value = i
    }
    const isMessageTabOpen = computed(() => {
      return menuItems.value[activeMenuItem.value]?.isMessageTab
    })

    const reportModalOpen = ref(false)

    const reportModalData = ref(null)

    onMounted(() => {
      EventBus.$on('showConnectionContext', (config) => {
        if (config) {
          config.connectionContext = true
          reportModalData.value = config
          reportModalOpen.value = true
        } else {
          reportModalOpen.value = false
        }
      })
    })

    onUnmounted(() => {
      store.dispatch('connections/dispatchClearReports')
    })

    return {
      CONTACT_KEYS,
      connection,
      hasAdditionalContactInfo,
      viewContactInfo,
      menuItems,
      activeMenuItem,
      isActiveMenuItem,
      setActiveMenuItem,
      isMessageTabOpen,
      reportModalOpen,
      reportModalData,
      isExampleModal,
    }
  },
})
</script>
